import { MAPS } from '@/config/maps';
import { SPAWNS, WAVE_LAYOUT } from '@/config/waves';
import { t } from '@/i18n';
import type { Arena } from '@/arena/arena';
import { framePoint, rectCenter, rectContains, type Piece } from '@/arena/geometry';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import { sfx } from '@/audio/sfx';
import type { RunState } from './run-state';
import { segmentWaveEnds } from './wave-plan';

/** What the advance director needs from the wave director. */
export interface AdvanceHost {
  /** Spawn wave `run.wave.index` in the current frame piece */
  startWave(): void;
  /** Unleash the held boss (chamber reached) */
  releaseBoss(): void;
  /** The death screen is up — nothing may advance under it */
  fightOver(): boolean;
}

type Phase = 'fight' | 'open' | 'boss';


/**
 * ADVANCE room flow (docs/01 advance rooms): each segment's fight starts
 * when the ship gets `triggerDepth` past its entry door (the door behind
 * seals), its share of the room's seeded waves runs, and clearing it
 * dissolves the exit gate. The last gate leads into the boss chamber; the
 * boss is released when the ship enters it. Failsafes: wave stall
 * detonation, a stalled gate forces open, a lost ship is rescued.
 */
export class AdvanceDirector {
  active = false;
  private phase: Phase = 'fight';
  /** Piece whose fight runs (fight) / was just cleared (open) */
  private piece = 0;
  /** Cumulative wave index at the end of each segment */
  private ends: number[] = [];
  private delay = 0;
  private gateStall = 0;
  private lost = 0;
  private readonly bossHome = { x: 0, y: 0 };
  private readonly anchor = { x: 0, y: 0 };

  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly hud: Hud,
    private readonly host: AdvanceHost,
  ) {}

  /** Room start on a route. `retry` resumes at the saved wave's segment. */
  begin(arena: Arena, waves: number, retry: boolean): void {
    const { world, run } = this;
    const wave = run.wave;
    const segments = arena.pieces.length - 1;
    this.active = true;
    this.ends = segmentWaveEnds(waves, segments);
    wave.total = this.ends[segments - 1] ?? 0;
    if (!retry) wave.index = 0;
    wave.index = Math.min(wave.index, wave.total);
    this.lost = 0;
    this.gateStall = 0;
    const boss = world.boss;
    if (boss) {
      this.bossHome.x = boss.x;
      this.bossHome.y = boss.y;
    }

    let start = this.ends.findIndex((end) => wave.index < end);
    if (start < 0) start = segments; // every wave done → straight to the boss
    if (start > 0) this.moveShip(arena.entryPoint(start));
    if (start >= segments) {
      this.enterChamber();
      return;
    }
    wave.bossHeld = true;
    this.startSegment(start, start === 0 ? MAPS.failsafe.firstWaveDelay : 0);
  }

  /** Room left / classic arena: idle. */
  reset(): void {
    this.active = false;
    this.world.arena.waypoint.active = false;
  }

  /** Wave-phase clock (boss held). */
  tick(dt: number): void {
    this.world.arena.tick(dt);
    this.rescueLostShip(dt);
    if (this.phase === 'fight') this.tickFight(dt);
    else if (this.phase === 'open') this.tickOpen();
  }

  /** Boss-phase clock: gate animations only. */
  tickBoss(dt: number): void {
    this.world.arena.tick(dt);
  }

  private startSegment(index: number, delay: number): void {
    const { world } = this;
    const arena = world.arena;
    const piece = arena.pieces[index]!;
    this.piece = index;
    this.phase = 'fight';
    this.delay = delay;
    this.gateStall = 0;
    arena.frame = piece;
    arena.waypoint.active = false;
    world.confineMinions(piece);
    this.holdBossAt(piece);
    // Gate guards join the segment's fight (they count toward the clear)
    const ship = world.player;
    for (const tp of arena.turrets[index] ?? []) {
      const o = piece.outer;
      const p = arena.clearAt(
        o.x + tp.x * o.w, o.y + tp.y * o.h, ship.x, ship.y,
        SPAWNS.turret.away, SPAWNS.turret.clear, piece,
      );
      world.minions.spawn('turret', 1, p.x, p.y);
    }
    if (delay <= 0) this.host.startWave();
  }

  private tickFight(dt: number): void {
    const { world, run } = this;
    const wave = run.wave;
    if (this.delay > 0) {
      this.delay -= dt;
      if (this.delay <= 0) this.host.startWave();
      return;
    }
    wave.stallTimer += dt;
    if (wave.stallTimer > WAVE_LAYOUT.stallSeconds && world.minions.count > 0) {
      const blast = world.minions.nearest(world.player.x, world.player.y);
      world.minions.clear();
      if (blast) world.fx.bigExplosion(blast.x, blast.y);
      sfx.explosion(WAVE_LAYOUT.stallBlastSfx);
    }
    if (world.minions.count > 0 || this.host.fightOver()) {
      this.gateStall = 0;
      return;
    }
    // Failsafe clock: an empty segment can never keep its gate shut
    this.gateStall += dt;
    const end = this.ends[this.piece] ?? wave.total;
    if (wave.index < end) {
      wave.index++;
      world.gems.spawnBurst(world.player.x, world.player.y, WAVE_LAYOUT.clearGems, 1, run.rng);
      if (wave.index < end) {
        this.host.startWave();
        return;
      }
    }
    if (wave.index >= end || this.gateStall > MAPS.failsafe.gateStall) this.openGate();
  }

  private openGate(): void {
    const { world } = this;
    const arena = world.arena;
    const door = arena.doors[this.piece]!;
    arena.setDoor(this.piece, false);
    this.phase = 'open';
    const g = door.gate;
    const c = rectCenter(g);
    const G = MAPS.gates;
    const along = g.w > g.h;
    for (let i = 0; i < G.openSparks; i++) {
      const k = (i + 0.5) / G.openSparks - 0.5;
      world.fx.explosion(c.x + (along ? k * g.w : 0), c.y + (along ? 0 : k * g.h), G.sparkScale);
    }
    world.camera.shake(G.openShake);
    sfx.portal();
    const toChamber = this.piece + 1 >= arena.pieces.length - 1;
    this.hud.notify(t(toChamber ? 'map.chamberOpen' : 'map.gateOpen'), 'portal');
    this.setWaypoint();
  }

  private tickOpen(): void {
    const arena = this.world.arena;
    const ship = this.world.player;
    const next = arena.pieces[this.piece + 1]!;
    this.setWaypoint();
    if (!this.pastTrigger(next, ship.x, ship.y)) return;
    arena.setDoor(this.piece, true); // seals behind the ship
    if (next.index >= arena.pieces.length - 1) this.enterChamber();
    else this.startSegment(next.index, 0);
  }

  /** Inside `piece` and `triggerDepth` past its entry side? */
  private pastTrigger(piece: Piece, x: number, y: number): boolean {
    if (!rectContains(piece.inner, x, y)) return false;
    const d = MAPS.route.triggerDepth;
    const o = piece.outer;
    switch (piece.dir) {
      case 'right': return x > o.x + d;
      case 'left': return x < o.x + o.w - d;
      case 'down': return y > o.y + d;
      default: return y < o.y + o.h - d;
    }
  }

  /** Waypoint for the arrow + pilots: the open door, then into the next piece. */
  private setWaypoint(): void {
    const arena = this.world.arena;
    const ship = this.world.player;
    const door = arena.doors[this.piece]!;
    const r = door.rect;
    const wp = arena.waypoint;
    wp.active = true;
    wp.chamber = this.piece + 1 >= arena.pieces.length - 1;
    wp.x = r.x + r.w / 2;
    wp.y = r.y + r.h / 2;
    const through = rectContains(r, ship.x, ship.y) ||
      rectContains(arena.pieces[this.piece + 1]!.inner, ship.x, ship.y);
    if (!through) return;
    // Through the doorway: aim past the trigger line, straight ahead
    const depth = MAPS.route.triggerDepth * MAPS.route.waypointDepth + Math.max(r.w, r.h) / 2;
    if (door.dir === 'up') wp.y -= depth;
    else if (door.dir === 'down') wp.y += depth;
    else if (door.dir === 'left') wp.x -= depth;
    else wp.x += depth;
  }

  private enterChamber(): void {
    const { world } = this;
    const arena = world.arena;
    this.phase = 'boss';
    this.piece = arena.pieces.length - 1;
    arena.frame = arena.fight;
    arena.waypoint.active = false;
    world.confineMinions(arena.fight);
    const boss = world.boss;
    if (boss) {
      boss.x = boss.prevX = this.bossHome.x;
      boss.y = boss.prevY = this.bossHome.y;
    }
    this.run.wave.index = this.run.wave.total;
    this.host.releaseBoss();
  }

  /** Park the held boss near the exit gate (drone squads orbit it). */
  private holdBossAt(piece: Piece): void {
    const boss = this.world.boss;
    if (!boss) return;
    framePoint(piece, MAPS.route.holdAnchor.u, MAPS.route.holdAnchor.v, this.anchor);
    boss.x = boss.prevX = this.anchor.x;
    boss.y = boss.prevY = this.anchor.y;
  }

  /** Failsafe: a ship outside the live pieces is brought back to the entry. */
  private rescueLostShip(dt: number): void {
    const arena = this.world.arena;
    const ship = this.world.player;
    const at = arena.pieceAt(ship.x, ship.y);
    const ahead = arena.doors[this.piece];
    const behind = arena.doors[this.piece - 1];
    // Doorway alcoves on either side of the live piece are fine to stand in
    const ok = at === this.piece ||
      (behind !== undefined && rectContains(behind.rect, ship.x, ship.y)) ||
      (ahead !== undefined && rectContains(ahead.rect, ship.x, ship.y)) ||
      (this.phase === 'open' && at === this.piece + 1);
    this.lost = ok ? 0 : this.lost + dt;
    if (this.lost < MAPS.failsafe.lostShip) return;
    this.lost = 0;
    this.moveShip(arena.entryPoint(this.piece));
  }

  private moveShip(p: { x: number; y: number }): void {
    this.world.player.spawnAt(p.x, p.y);
    this.world.camera.snapTo(p.x, p.y);
  }
}
